import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CardMedia,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Article as ArticleIcon,
  CheckCircle as PublishedIcon,
  Schedule as DraftIcon,
} from '@mui/icons-material';

interface Post {
  id: string;
  title: string;
  type: 'video' | 'image';
  likes: number;
  comments: number;
  status: 'published' | 'draft';
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

// Mock data
const mockPosts: Post[] = [
  {
    id: '1',
    title: 'Como pagar com o eCard Pagali Wallet',
    type: 'video',
    likes: 0,
    comments: 0,
    status: 'published',
    createdAt: '2023-03-24T21:24:17',
    updatedAt: '2023-03-24T21:28:30',
  },
  {
    id: '2',
    title: 'Bilou Beach Presents DED and FATU DJASSI',
    type: 'image',
    likes: 0,
    comments: 0,
    status: 'published',
    createdAt: '2023-07-31T15:23:33',
    updatedAt: '2023-07-31T15:23:58',
  },
  {
    id: '3',
    title: 'Farol e Electra',
    type: 'image',
    likes: 0,
    comments: 0,
    status: 'published',
    createdAt: '2016-10-19T10:20:22',
    updatedAt: '2016-10-19T10:20:22',
  },
  {
    id: '4',
    title: 'Bilou Beach presenta Felicce and Tansha Cunha',
    type: 'image',
    likes: 0,
    comments: 0,
    status: 'published',
    createdAt: '2023-07-24T20:55:11',
    updatedAt: '2023-07-24T20:55:11',
  },
  {
    id: '5',
    title: 'Contas de Telefone via Internet',
    type: 'image',
    likes: 28,
    comments: 13,
    status: 'published',
    createdAt: '2016-10-11T11:12:40',
    updatedAt: '2016-10-11T11:12:35',
  },
  {
    id: '6',
    title: 'Pode pagar suas citas Seguro',
    type: 'image',
    likes: 10,
    comments: 3,
    status: 'published',
    createdAt: '2016-10-13T11:24:39',
    updatedAt: '2016-10-13T11:24:42',
  },
  {
    id: '7',
    title: 'Mark Delimas e Amigos',
    type: 'image',
    likes: 0,
    comments: 0,
    status: 'published',
    createdAt: '2023-07-29T10:01:28',
    updatedAt: '2023-07-29T10:03:15',
  },
  {
    id: '8',
    title: 'Campanha VGBB',
    type: 'image',
    likes: 3,
    comments: 0,
    status: 'published',
    createdAt: '2023-08-01T21:23:31',
    updatedAt: '2023-08-01T21:23:03',
  },
  {
    id: '9',
    title: 'PAGALI, SPOT 2',
    type: 'video',
    likes: 0,
    comments: 1,
    status: 'published',
    createdAt: '2016-10-17T12:28:18',
    updatedAt: '2016-10-17T12:28:18',
  },
  {
    id: '10',
    title: 'Criar e Jax eCard Pagali Wallet',
    type: 'video',
    likes: 0,
    comments: 0,
    status: 'published',
    createdAt: '2023-04-12T21:38:08',
    updatedAt: '2023-04-12T21:38:46',
  },
];

const Posts: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>(mockPosts);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [formData, setFormData] = useState<Partial<Post>>({
    title: '',
    type: 'image',
    status: 'draft',
  });

  const getStatusChip = (status: string) => {
    return status === 'published' ? (
      <Chip size="small" color="success" label="Publicado" icon={<PublishedIcon />} />
    ) : (
      <Chip size="small" color="default" label="Rascunho" icon={<DraftIcon />} />
    );
  };

  const getTypeIcon = (type: string) => {
    return type === 'video' ? (
      <VideoIcon color="error" />
    ) : (
      <ImageIcon color="primary" />
    );
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchTerm === '' ||
      post.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || post.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const stats = {
    total: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    videos: posts.filter(p => p.type === 'video').length,
    images: posts.filter(p => p.type === 'image').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (post?: Post) => {
    if (post) {
      setSelectedPost(post);
      setFormData({
        title: post.title,
        type: post.type,
        status: post.status,
      });
    } else {
      setSelectedPost(null);
      setFormData({
        title: '',
        type: 'image',
        status: 'draft',
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedPost(null);
  };

  const handleSave = () => {
    if (selectedPost) {
      setPosts(posts.map(p =>
        p.id === selectedPost.id
          ? { ...p, ...formData, updatedAt: new Date().toISOString() }
          : p
      ));
    } else {
      const newPost: Post = {
        id: crypto.randomUUID(),
        ...formData as Omit<Post, 'id' | 'likes' | 'comments' | 'createdAt' | 'updatedAt'>,
        likes: 0,
        comments: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setPosts([newPost, ...posts]);
    }
    handleCloseEdit();
  };

  const handleDelete = (post: Post) => {
    if (window.confirm(`Tem certeza que deseja eliminar o post "${post.title}"?`)) {
      setPosts(posts.filter(p => p.id !== post.id));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Posts / Feeds
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          Novo Post
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArticleIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Posts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PublishedIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.published}</Typography>
                  <Typography variant="caption" color="text.secondary">Publicados</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VideoIcon color="error" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.videos}</Typography>
                  <Typography variant="caption" color="text.secondary">Vídeos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.images}</Typography>
                  <Typography variant="caption" color="text.secondary">Imagens</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 9 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Tipo de Feed"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="video">Vídeo</MenuItem>
                <MenuItem value="image">Imagem</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Posts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}></TableCell>
                <TableCell>Título</TableCell>
                <TableCell width={120}>Tipo de Feed</TableCell>
                <TableCell width={80} align="center">Likes</TableCell>
                <TableCell width={100} align="center">Comentários</TableCell>
                <TableCell width={150}>Criado em</TableCell>
                <TableCell width={150}>Actualizado em</TableCell>
                <TableCell width={120} align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPosts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((post) => (
                <TableRow key={post.id} hover>
                  <TableCell>
                    <Avatar sx={{ bgcolor: post.type === 'video' ? 'error.light' : 'primary.light' }}>
                      {getTypeIcon(post.type)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {post.title}
                    </Typography>
                    {getStatusChip(post.status)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={post.type}
                      color={post.type === 'video' ? 'error' : 'primary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{post.likes}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{post.comments}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(post.createdAt).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(post.updatedAt).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenEdit(post)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(post)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredPosts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon />
          {selectedPost ? 'Editar Post' : 'Novo Post'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Título"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Tipo de Feed"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'video' | 'image' })}
              >
                <MenuItem value="image">Imagem</MenuItem>
                <MenuItem value="video">Vídeo</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'published' | 'draft' })}
              >
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="published">Publicado</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="URL da Imagem/Vídeo"
                value={formData.imageUrl || ''}
                onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                helperText="URL do conteúdo multimédia"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.title}
          >
            {selectedPost ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Posts;
